import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { isDesktop } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { AppTracker, BottomPanelKey, ChannelSchema, EventSchema, EventType, UserActionType } from 'types';
import {
  displayDateTime,
  getChannelURL,
  isValidURL,
  SHOW_BECOME_MEMBER_MODAL_INTERVAL,
  UZBEKITTV_CHANNEL_NAME
} from 'utils';
import { profileActions } from 'store/actions';
import {
  authSelector,
  hasAvailableMinutesSelector,
  hasChannelSelector,
  isMemberSelector,
  isSubscribedSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService, tracker } from 'services';
import { useBottomSheet, useConfirmation, useModal, useRouter, useStore, useToast } from 'hooks';
import {
  BecomeMemberModal,
  BottomPanels,
  ConfirmPanel,
  EventNotAvailableForPublic,
  Navbar,
  PastEvent
} from 'containers';
import { Switch, Text } from 'components';
import {
  CalendarIcon,
  CheckedIcon,
  DeleteIcon,
  LockClosedIcon,
  LockOpenedIcon,
  NoPhotoIcon,
  PlayIcon,
  ShareIcon,
  TVIcon
} from 'components/Icon';

import SharePanel from './SharePanel';

import styles from './PastEventDetail.module.scss';

export type EventDetailProps = {
  isUzbekITTV?: boolean;
  canWatch?: boolean;
  channel?: ChannelSchema;
  event: EventSchema;
  onStarted?(channelName: string, eventCode: string, options: { audioMuted: boolean; videoMuted: boolean }): void;
  onEdited?(updatedEvent: EventSchema): void;
  onClose?(): void;
  onBecomeMember?(): void;
  onToggleVisibility?(visible: boolean): void;
};

const PastEventDetail = ({
  isUzbekITTV = false,
  canWatch = true,
  channel,
  event,
  onClose,
  onBecomeMember,
  onToggleVisibility
}: EventDetailProps): JSX.Element => {
  const { channelName, poster, hostUsername, startDate, code, name, memberOnly, description, hidePastEvent } = event;

  const toast = useToast();
  const router = useRouter();
  const dispatch = useDispatch();
  const becomeMemberModal = useModal();
  const bottomSheet = useBottomSheet<BottomPanelKey.SHARE | BottomPanelKey.LOGIN | BottomPanelKey.PAID_CONTENT>();
  const confirmation = useConfirmation<'DELETE_EVENT'>();
  const { auth, isSubscribed, isMember } = useStore(state => ({
    auth: authSelector(state),
    isSubscribed: isSubscribedSelector(event.channelName)(state),
    isMember: isMemberSelector(channelName)(state),
    hasAvailableMinutes: hasAvailableMinutesSelector(state),
    hasOwnChannel: hasChannelSelector(state)
  }));

  const [posterLoadFailed, setPosterLoadFailed] = useState(!poster);
  const [preview, setPreview] = useState(true);

  const [isPaywallVisible, setPaywallVisibility] = useState(false);

  const isOwnEvent = hostUsername === auth.username;
  const hasMembershipPlan = !!channel?.membershipPricingPlan;
  const canSubscribeToChannel = !isOwnEvent && !!channel;

  useEffect(() => {
    tracker.openEventDetailPage(event.code);
  }, []);

  // reset failed state when poster is changed
  useEffect(() => {
    setPosterLoadFailed(!poster);
  }, [poster]);

  // show become member modal (annoying modal) for unpaid viewers periodically to urge them to pay
  useEffect(() => {
    if (preview) return () => {};

    const disablePictureInPictureMode = async () => {
      try {
        await document.exitPictureInPicture();
      } catch (e) {
        LoggerService.log('DISABLE_PIP_MODE_ON_PAST_EVENT', e);
      }
    };

    if (channelName.toLocaleLowerCase() === UZBEKITTV_CHANNEL_NAME && !isMember && !becomeMemberModal.isOpen) {
      const interval = setInterval(() => {
        setPaywallVisibility(true);
        becomeMemberModal.open();

        if (document.pictureInPictureElement) disablePictureInPictureMode();
      }, SHOW_BECOME_MEMBER_MODAL_INTERVAL);

      return () => clearInterval(interval);
    }

    return () => {};
  }, [isMember, preview]);

  const handleOnOpenSharePanel = () => {
    bottomSheet.open(BottomPanelKey.SHARE);
  };

  const handleOnPosterLoadFailed = useCallback(() => setPosterLoadFailed(true), []);

  const handleOnSubscribe = () => {
    if (!auth.isAuth) {
      dispatch(
        callbackTriggersActions.set([
          {
            type: UserActionType.REDIRECT,
            payload: { path: router.pathname }
          }
        ])
      );

      router.push('/login');
    } else {
      subscribe();
    }
  };

  const handleOnDeletionCancelled = async () => {
    confirmation.close();
  };

  const handleOnDeletionSubmitted = async () => {
    try {
      await api.requests.ltv.deletePastEvent(code);

      if (router.canGoBack) {
        router.goBack();
      } else {
        router.replace(getChannelURL(channelName));
      }

      confirmation.close();
    } catch (e) {
      LoggerService.log('DELETE_PAST_EVENT', e);
      if (e instanceof Error) {
        toast.error(e, { id: 'DELETE_PAST_EVENT' });
      }
    }
  };

  const handleOnDelete = () => {
    confirmation.open('DELETE_EVENT');
  };

  const handleOnPlay = () => {
    if (!event.memberOnly && (!event.recordingPlaybackUrl || !isValidURL(event.recordingPlaybackUrl))) {
      toast.info('Recording is not available', { id: 'RECORDING_NOT_AVAILABLE' });
    } else if (!canWatch) {
      bottomSheet.open(BottomPanelKey.PAID_CONTENT);
    } else {
      setPreview(false);
    }
  };

  const handleOnChangeVisibility = (value: boolean) => {
    onToggleVisibility?.(value);
  };

  const renderSubscribe = () => {
    // TODO: Find another solution for uzbekittv channel custom buttons
    if (isUzbekITTV) {
      return null;
    }

    if (!canSubscribeToChannel) {
      return null;
    }

    if (isSubscribed) {
      return (
        <button type="button" disabled className={cx(styles.action, styles.subscribe)}>
          <CheckedIcon className="mr-6" />
          <span>Subscribed</span>
        </button>
      );
    }

    return (
      <button type="button" className={cx(styles.action, styles.subscribe)} onClick={handleOnSubscribe}>
        Subscribe
      </button>
    );
  };

  const subscribe = async () => {
    if (!channel) return;

    try {
      tracker.subscribeToChannel(channel.id, channel.name, AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE);
      dispatch(profileActions.addChannel(channel));

      await api.requests.ltv.subscribeToChannel(channel.id);
      tracker.subscribeToChannelSuccess(
        channel.id,
        channel.name,
        AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE
      );

      api.requests.ltv
        .getSubscribedChannels()
        .then(response => {
          dispatch(profileActions.setChannels(response.data));
        })
        .catch(e => {
          LoggerService.log('GET_SUBSCRIBED_CHANNELS', e);
        });

      toast.success('Subscribed!', { id: `SUBSCRIBE_TO_CHANNEL:${channel.id}` });
    } catch (error) {
      LoggerService.log('SUBSCRIBE_TO_CHANNEL_ON_EVENT_DETAIL', error);

      if (error instanceof Error) {
        tracker.subscribeToChannelFail(
          channel.id,
          channel.name,
          AppTracker.ChannelSubscriptionSource.EVENT_DETAIL_PAGE,
          error
        );
      }
      dispatch(profileActions.removeChannel({ channelId: channel.id }));

      toast.error('Subscribe request is failed', { id: `SUBSCRIBE_TO_CHANNEL:${channel.id}` });
    }
  };

  const handleOnPaywallAttempted = () => {
    becomeMemberModal.close();
    bottomSheet.open(BottomPanelKey.LOGIN);
  };

  const renderBecomeMember = () => {
    if (isOwnEvent) {
      return null;
    }

    if (isMember) {
      return (
        <button type="button" className={cx(styles.action, styles.becomeMember)} disabled>
          <CheckedIcon className="mr-4" />
          <span>{isUzbekITTV ? 'Siz obunachisiz' : 'You’re a member'}</span>
        </button>
      );
    }

    if (hasMembershipPlan) {
      return (
        <button type="button" className={cx(styles.action, styles.becomeMember)} onClick={onBecomeMember}>
          {/* // TODO: Find another solution for uzbekittv channel custom buttons */}
          {isUzbekITTV ? "Obuna bo'lish" : 'Become a member'}
        </button>
      );
    }

    return null;
  };

  const renderPoster = () =>
    posterLoadFailed || !poster ? (
      <div className={styles.placeholder}>
        <NoPhotoIcon />
        <span>No cover photo</span>
      </div>
    ) : (
      <>
        <img className={styles.cover} src={poster} alt="poster" />
        <img className={styles.image} src={poster} alt="poster" onError={handleOnPosterLoadFailed} />
      </>
    );

  const renderDetails = () => (
    <>
      <div className={styles.poster}>{renderPoster()}</div>
      <div className={styles.content}>
        <h3 className={styles.title}>{name}</h3>
        {description && (
          <Text size="h5" className={styles.description}>
            {description}
          </Text>
        )}

        <ul className={styles.extra}>
          <li>
            <div className={styles.item}>
              <CalendarIcon className="mr-4" width={18} height={18} color="#4FC940" />
              <span>{displayDateTime(startDate)}</span>
            </div>
          </li>
          <li>
            <Link to={`/${event.channelName}`} onClick={onClose}>
              <div className={styles.item}>
                <TVIcon className="mr-4" width={18} height={18} color="#4FC940" />
                <span>@{event.channelName}</span>
              </div>
            </Link>
          </li>
          {memberOnly && (
            <li>
              <div className={styles.item}>
                {isMember ? (
                  <LockOpenedIcon className="mr-4" width={18} height={18} color="#4FC940" />
                ) : (
                  <LockClosedIcon className="mr-4" width={18} height={18} color="#FE514E" />
                )}
                <span>Members Only</span>
              </div>
            </li>
          )}
        </ul>

        {isOwnEvent ? (
          <div className={cx(styles.actions, styles.host)}>
            <Switch
              title="Visible to everyone"
              className={styles.visibility}
              checked={!hidePastEvent}
              onChange={handleOnChangeVisibility}
            />
            <button type="button" className={cx(styles.action, styles.delete)} onClick={handleOnDelete}>
              <DeleteIcon />
              <span>Delete</span>
            </button>
            <button type="button" className={cx(styles.action, styles.play)} onClick={handleOnPlay}>
              <PlayIcon />
              <span>Play recording</span>
            </button>
          </div>
        ) : (
          <div className={cx(styles.actions, styles.viewer)}>
            {canSubscribeToChannel && (
              <>
                {renderSubscribe()}
                {memberOnly && renderBecomeMember()}
              </>
            )}
            <button type="button" className={cx(styles.action, styles.play)} onClick={handleOnPlay}>
              <PlayIcon />
              <span>Play recording</span>
            </button>
          </div>
        )}
      </div>
    </>
  );

  const renderContent = () => {
    if (preview) {
      return <div className={styles.container}>{renderDetails()}</div>;
    }

    return <PastEvent eventCode={event.code} playbackUrl={event.recordingPlaybackUrl} disabled={isPaywallVisible} />;
  };

  return (
    <>
      <Navbar
        className={styles.header}
        title="Past event"
        right={
          <button type="button" className={styles.share} onClick={handleOnOpenSharePanel}>
            <ShareIcon width={24} height={24} />
          </button>
        }
      />

      {renderContent()}

      <BottomPanels
        inner
        panels={[
          {
            name: BottomPanelKey.SHARE,
            show: bottomSheet.state.contentKey === BottomPanelKey.SHARE,
            title: isDesktop ? 'Share it via social media' : 'Share this event',
            component: <SharePanel event={event} />
          },
          {
            name: BottomPanelKey.PAID_CONTENT,
            show: bottomSheet.state.contentKey === BottomPanelKey.PAID_CONTENT,
            title: 'Content is not available',
            component: (
              <div className="py-24">
                <EventNotAvailableForPublic isUzbekITTV={isUzbekITTV} onPurchase={onBecomeMember} />
              </div>
            )
          }
        ]}
        onClose={bottomSheet.close}
      />

      <BecomeMemberModal
        channelName={channelName}
        eventType={EventType.PAST_EVENT}
        eventCode={event.code}
        isOpen={becomeMemberModal.isOpen}
        onAuthRequired={handleOnPaywallAttempted}
      />

      <ConfirmPanel
        show={confirmation.state.isOpen}
        name="DELETE_EVENT"
        title="Do you want to delete this event?"
        message="You cannot restore the event after deleting"
        submitText="Delete event"
        onCancel={handleOnDeletionCancelled}
        onSubmit={handleOnDeletionSubmitted}
      />
    </>
  );
};

export default PastEventDetail;
